import React from 'react';
import { Box, Typography, Button, Container, useTheme, useMediaQuery, Tabs, Tab } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PersonIcon from '@mui/icons-material/Person';

const EcosystemHero = ({ activeTab, setActiveTab }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const heroContent = {
    0: {
      titlePrefix: "Ecosistema All-In-One para",
      titleHighlight: "Empresas",
      description: "Escala tu negocio centralizando ventas, marketing y gestión. Encuentra talento global en la Bolsa de Profesionales, gestiona prospectos con nuestro CRM, extrae leads calificados y administra proyectos en un solo lugar.",
      cta: "Crear Cuenta de Empresa"
    },
    1: {
      titlePrefix: "Ecosistema All-In-One para",
      titleHighlight: "Profesionales",
      description: "Impulsa tu carrera al siguiente nivel. Fórmate en la Academia HitPoly con habilidades de alta demanda, conecta con la comunidad, y accede a la Bolsa de Profesionales para ser contratado por empresas globales.",
      cta: "Únete como Profesional"
    }
  };

  const currentContent = heroContent[activeTab];

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#0B0914',
        color: '#fff',
        pt: { xs: 15, md: 18 },
        pb: { xs: 8, md: 12 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          background: activeTab === 0 
            ? 'radial-gradient(circle, rgba(108,77,226,0.15) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle, rgba(242,28,99,0.15) 0%, rgba(0,0,0,0) 70%)',
          transition: 'background 0.5s ease',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: '60vw',
          height: '60vw',
          background: activeTab === 0
            ? 'radial-gradient(circle, rgba(242,28,99,0.1) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle, rgba(41,182,246,0.1) 0%, rgba(0,0,0,0) 70%)',
          transition: 'background 0.5s ease',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        
        {/* Toggle Empresas / Profesionales */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
          <Box sx={{ 
            background: 'rgba(255,255,255,0.05)', 
            borderRadius: '50px', 
            p: 0.5,
            border: '1px solid rgba(255,255,255,0.1)' 
          }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, nv) => setActiveTab(nv)}
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: '40px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: '40px',
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'rgba(255,255,255,0.5)',
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    color: '#fff',
                    background: activeTab === 0 
                      ? 'linear-gradient(45deg, #6C4DE2, #4b32a3)' 
                      : 'linear-gradient(45deg, #F21C63, #b81047)',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                  }
                }
              }}
            >
              <Tab icon={<BusinessCenterIcon sx={{ mr: 1, fontSize: 18 }} />} iconPosition="start" label="Empresas" />
              <Tab icon={<PersonIcon sx={{ mr: 1, fontSize: 18 }} />} iconPosition="start" label="Profesionales" />
            </Tabs>
          </Box>
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              variant={isMobile ? 'h3' : 'h1'}
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                lineHeight: 1.1,
                letterSpacing: '-1px',
              }}
            >
              {currentContent.titlePrefix} <br />
              <Box
                component="span"
                sx={{
                  background: activeTab === 0 
                    ? 'linear-gradient(90deg, #6C4DE2, #F21C63)'
                    : 'linear-gradient(90deg, #F21C63, #29B6F6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {currentContent.titleHighlight}
              </Box>
            </Typography>

            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: '800px',
                mx: 'auto',
                mb: 5,
                fontWeight: 400,
              }}
            >
              {currentContent.description}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                sx={{
                  background: activeTab === 0 
                    ? 'linear-gradient(45deg, #6C4DE2, #F21C63)'
                    : 'linear-gradient(45deg, #F21C63, #29B6F6)',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: activeTab === 0 
                    ? '0 4px 15px rgba(108,77,226,0.4)'
                    : '0 4px 15px rgba(242,28,99,0.4)',
                  '&:hover': {
                    filter: 'brightness(1.1)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                  },
                }}
              >
                {currentContent.cta} <ArrowForwardIcon sx={{ ml: 1 }} />
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  document.getElementById('ecosystem-features')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#fff',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Explorar Herramientas
              </Button>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default EcosystemHero;
